import { useSortable } from '@dnd-kit/sortable';
import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { cloneElement, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { Accordion } from '@/components/Accordion/Accordion';
import {
  getIconForSemanticTileNode,
  getSemanticTileColors,
} from '@/components/diagrams/components/Tile/Tile.utils';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Settings04Icon } from '@/components/icons/Settings04Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Draghandle } from '@/components/lists/DragAndDropList/Draghandle';
import { getSortableItemStyle } from '@/components/lists/DragAndDropList/getSortableItemStyle';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { deriveRecipientKindFromId } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.helpers';
import { hasOnlyInsurancePolicies } from '@/modules/dispositiveProvisions/dispositiveProvisions.formatting';
import {
  dispositionKindOptions,
  getTransferTaxKindOptions,
  otherDispositionKindOptions,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { AdvancedSettingsCard } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/AdvancedSettingsCard';
import { AssetDistributionCallout } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/AssetDistributionCallout';
import { AssetSelectorDropdown } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/AssetSelectorDropdown';
import { RecipientsBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient';
import { DispositiveProvisionsTemplateRules } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.constants';
import {
  getDispositionKindOption,
  getDispositionKindsToDisable,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.utils';
import { DistributionSummaryCallout } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DistributionSummaryCallout';
import { getDistributionSummary } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DraggableListItemRecipient.utils';
import { useSetRecipientField } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useSetRecipientField';
import { useSyncRecipientKind } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useSyncRecipientKind';
import { useRemainingExemptionAmountForClient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useRemainingExemptionAmountForClient';
import { useHasGrantorWithStateTax } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { RecipientSelectorModal } from '@/modules/dispositiveProvisions/RecipientSelectorModal';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { MUI_INPUT_HEIGHT_PX } from '@/styles/tokens/inputHeights';
import {
  DispositiveProvisionDispositionKind,
  EntityInEstateStatus,
  TransferMethod,
} from '@/types/schema';
import { PathsOf } from '@/types/subform';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from './BeneficiaryDesignationsForm.types';
import { useBdgDyingClientId } from './hooks/useBdgDyingClientId';
import { useBdgResiduaryPaysAllTaxes } from './hooks/useBdgResiduaryPaysAllTaxes';
import { useBeneficiaryDesignationRecipientOptions } from './hooks/useBeneficiaryDesignationRecipientOptions';
import { useDefaultBdgRecipientBearsTax } from './hooks/useDefaultBdgRecipientBearsTax';
import { useDefaultBdgRecipientTransferTaxKind } from './hooks/useDefaultBdgRecipientTransferTaxKind';

interface BeneficiaryDesignationRecipientProps {
  recipientId: string;
  index: number;
  tier: 'primary' | 'contingent';
  onRemove: () => void;
  forceExpandIds: string[];
  defaultExpandAll: boolean;
  disabled?: boolean;
  entityIdOverride?: string;
  entityType: EntityType;
  isOnHypotheticalWaterfall: boolean;
}

export function BeneficiaryDesignationRecipient({
  recipientId,
  index,
  tier,
  onRemove,
  forceExpandIds,
  defaultExpandAll,
  disabled = false,
  entityIdOverride,
  entityType,
  isOnHypotheticalWaterfall,
}: BeneficiaryDesignationRecipientProps) {
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const { control, setValue } =
    useFormContext<BeneficiaryDesignationsFormShape>();
  const { entityId } = useEntityDetailsContext();
  const { householdId, primaryClients, isTwoClientHousehold } =
    useHouseholdDetailsContext();

  const [isRecipientSelectorModalOpen, setIsRecipientSelectorModalOpen] =
    useState(false);

  const basePath =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients` as const;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: recipientId,
    disabled,
  });

  const style = getSortableItemStyle({ transform, transition, isDragging });

  // Watch the fields used by the accordion header display in one call.
  const [
    selectedRecipientId,
    selectedDistributionKind,
    selectedDistributionKindSelect,
    selectedTransferTaxKind,
    dispositionPercentage,
    dispositionAmount,
    notes,
  ] = useWatch({
    control,
    name: [
      `${basePath}.${index}.recipientId`,
      `${basePath}.${index}.dispositionKind_ButtonGroup`,
      `${basePath}.${index}.dispositionKind_Select`,
      `${basePath}.${index}.transferTaxKind`,
      `${basePath}.${index}.dispositionPercentage`,
      `${basePath}.${index}.dispositionAmount`,
      `${basePath}.${index}.notes`,
    ],
  });

  // Notes are collapsed by default. We surface an "Add notes" toggle in the
  // footer (matches DP recipient card behavior).
  const [notesAreExpanded, setNotesAreExpanded] = useState(Boolean(notes));

  const isPercentDistributionKind =
    selectedDistributionKind === DispositiveProvisionDispositionKind.Percentage;
  const isAmountDistributionKind =
    selectedDistributionKind === DispositiveProvisionDispositionKind.Amount;
  const isFormulaDistributionKind = selectedDistributionKind === 'other';
  const isAssetDistributionKind = selectedDistributionKind === 'asset';

  // BDG reuses the asset selector and other DP recipient subcomponents that
  // accept a `RecipientsBasePath`. The path tag is widened to include BDG.
  const recipientsBasePath = basePath as RecipientsBasePath;

  const { options, typeaheadOptions, metadataById, recipientLists } =
    useBeneficiaryDesignationRecipientOptions({
      entityIdOverride,
      isOnHypotheticalWaterfall,
    });

  // Default the recipient type based on the recipient's relationship to the
  // account's decedent, matching the DP recipient form (e.g. a child defaults
  // to "Generation 2 or other individual").
  useDefaultBdgRecipientTransferTaxKind({ tier, index, recipientLists });

  // Default each recipient's bearsTax from its recipient type (taxable bears
  // tax; charitable / marital do not) when the tier's residuary toggle is off.
  useDefaultBdgRecipientBearsTax({ tier, index });

  // BDG allows the same set of recipient types as a non-templated DP, so we
  // reuse the NO_TEMPLATE recipient configuration to drive the "create new"
  // recipient chooser modal.
  const recipientConfiguration =
    DispositiveProvisionsTemplateRules.NO_TEMPLATE.recipientConfiguration;

  const recipientDisplay = useMemo(() => {
    if (!selectedRecipientId) return 'Recipient';
    const option = options.find((o) => o.value === selectedRecipientId);
    return option?.display ?? 'Recipient';
  }, [selectedRecipientId, options]);

  // Retirement accounts paid to an individual or surviving spouse distribute
  // outright; nudge the user toward an inherited retirement account. Insurance
  // accounts have no such concept, so the callout is retirement-only.
  const selectedRecipientKind = selectedRecipientId
    ? deriveRecipientKindFromId(selectedRecipientId)
    : null;
  const showOutrightDistributionCallout =
    entityType === 'retirement-account' &&
    (selectedRecipientKind === RecipientKind.Individual ||
      selectedRecipientKind === RecipientKind.SurvivingSpouse);

  // Determine whether the selected recipient IS the surviving spouse (either
  // via the sentinel or by selecting a primary client by name) so the callout
  // can show the appropriate copy variant.
  const dyingClientId = useBdgDyingClientId();
  const isSurvivingSpouseRecipient =
    selectedRecipientKind === RecipientKind.SurvivingSpouse ||
    (selectedRecipientKind === RecipientKind.Individual &&
      primaryClients?.some((c) => c.id === selectedRecipientId) === true);

  // Keep the warning conversational; the selector uses the full display name.
  const calloutRecipientName = useMemo(() => {
    if (!showOutrightDistributionCallout || !selectedRecipientId) return null;
    if (isSurvivingSpouseRecipient) {
      // For the sentinel, the surviving spouse is the primary client who is
      // NOT the account owner/decedent.
      if (selectedRecipientKind === RecipientKind.SurvivingSpouse) {
        return (
          primaryClients?.find((c) => c.id !== dyingClientId)
            ?.displayGivenName ?? null
        );
      }
      // Selected a primary client by their actual ID.
      return (
        primaryClients?.find((c) => c.id === selectedRecipientId)
          ?.displayGivenName ?? null
      );
    }
    // Non-spouse individual: use the recipient query data.
    return (
      recipientLists.clientProfileRecipients.find(
        (c) => c.id === selectedRecipientId
      )?.displayGivenName ?? null
    );
  }, [
    showOutrightDistributionCallout,
    selectedRecipientId,
    isSurvivingSpouseRecipient,
    selectedRecipientKind,
    primaryClients,
    dyingClientId,
    recipientLists.clientProfileRecipients,
  ]);

  // Dynamic, light-tinted icon based on the selected recipient's semantic
  // kind (in-estate, out-of-estate, charitable, etc.). Falls back to navy.
  const tileNode = selectedRecipientId
    ? metadataById[selectedRecipientId]
    : undefined;
  const { bg: tileBg, fg: tileFg } = getSemanticTileColors(tileNode, 'subtle');
  const tileIconElement = useMemo(() => {
    const node = tileNode ?? {
      __typename: 'Entity' as const,
      inEstateStatus: EntityInEstateStatus.InEstate,
    };
    const icon = getIconForSemanticTileNode(node);
    return icon
      ? cloneElement(icon, {
          size: 20,
          color: tileFg,
          sx: { display: 'flex' },
        })
      : null;
  }, [tileNode, tileFg]);

  const transferTaxKindOptions = useMemo(
    () =>
      getTransferTaxKindOptions({
        hasStateTax: false,
      }),
    []
  );

  const hasStateTax = useHasGrantorWithStateTax();
  const { data: clientExemptionData } = useRemainingExemptionAmountForClient({
    clientId: dyingClientId,
  });
  const tierRecipients = useWatch({
    control,
    name: basePath,
  });

  // Keep recipientKind in sync with the selected recipient (matches the DP
  // recipient card) so the surviving-spouse sentinel and other kinds resolve
  // correctly in the diagram preview and on submit.
  useSyncRecipientKind({
    currentRecipient: tierRecipients?.[index],
    recipientsBasePath,
    idx: index,
  });

  const thisRecipient = tierRecipients?.[index];
  const setRecipientField = useSetRecipientField(recipientsBasePath, index);

  // The advanced-settings tray (transfer method, max distribution, % of asset,
  // grow by inflation, "net of taxes") mirrors the DP recipient card.
  const [advancedSettingsIsOpen, setAdvancedSettingsIsOpen] = useState(false);
  const showAdvancedSettingsCard =
    isPercentDistributionKind ||
    isAmountDistributionKind ||
    isFormulaDistributionKind ||
    isAssetDistributionKind;

  const advancedSettingsButton = (
    <IconButton
      icon={Settings04Icon}
      ariaLabel="Advanced settings"
      size="sm"
      variant="transparent"
      onClick={() => setAdvancedSettingsIsOpen(!advancedSettingsIsOpen)}
      sx={{
        height: MUI_INPUT_HEIGHT_PX,
        width: MUI_INPUT_HEIGHT_PX,
        backgroundColor: advancedSettingsIsOpen
          ? COLORS.NAVY[100]
          : 'transparent',
        '&:hover': {
          backgroundColor: COLORS.NAVY[100],
          borderColor: 'transparent',
        },
      }}
    />
  );

  // When the tier's residuary pays all taxes, per-recipient bearsTax is owned
  // by the tier toggle, so hide the per-recipient "net of taxes" checkbox
  // (matches the DP BearsTaxToggle, which returns null when the flag is on).
  const { residuaryPaysAllTaxes } = useBdgResiduaryPaysAllTaxes(tier);

  const bearsTaxCheckbox = (
    <FormAwareCheckbox<BeneficiaryDesignationsFormShape>
      label="Distribution is net of taxes"
      fieldName={
        `${basePath}.${index}.bearsTax` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
      }
      control={control}
      helpText="If toggled on, recipient will receive distribution specified above less any taxes owed"
    />
  );

  // For asset DPs: hide the transfer toggle when only insurance policies are
  // selected (insurance always transfers in kind).
  const isInsuranceOnlySelection = useMemo(() => {
    if (!isAssetDistributionKind || !thisRecipient) return false;
    return hasOnlyInsurancePolicies({
      insurancePolicies: thisRecipient.insurancePolicyIds?.length ?? 0,
      assetGroups: thisRecipient.assetGroupIds?.length ?? 0,
      assetStatics: thisRecipient.assetStaticIds?.length ?? 0,
      ownershipStakes: thisRecipient.ownershipStakeIds?.length ?? 0,
    });
  }, [isAssetDistributionKind, thisRecipient]);

  // When switching distribution kind to "asset", set defaults if no existing
  // values (matches the DP recipient card).
  const prevDistributionKind = usePrevious(selectedDistributionKind);
  useEffect(() => {
    if (prevDistributionKind === selectedDistributionKind) return;
    if (prevDistributionKind === undefined) return;

    if (
      selectedDistributionKind === 'asset' &&
      prevDistributionKind !== 'asset'
    ) {
      if (thisRecipient?.dispositionAssetPercentage == null) {
        setRecipientField('dispositionAssetPercentage', new Decimal(100), {
          shouldDirty: true,
        });
      }
      // Asset DPs default to InKind; always set on switch since defaultRecipient
      // pre-populates transferMethod as LiquidateToCash.
      setRecipientField('transferMethod', TransferMethod.InKind, {
        shouldDirty: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when distribution kind changes
  }, [selectedDistributionKind]);

  const dispositionOptionsToDisable = useMemo(
    () => getDispositionKindsToDisable(tierRecipients ?? []),
    [tierRecipients]
  );
  const dispositionKindSelectOptions = useMemo(
    () =>
      otherDispositionKindOptions.flatMap((option) => {
        if (
          option.value ===
          DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption
        ) {
          if (!hasStateTax) return [];
        }
        if (
          !hasStateTax &&
          [
            DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption,
            DispositiveProvisionDispositionKind.RemainingStateExemption,
          ].includes(option.value)
        ) {
          return [];
        }
        return getDispositionKindOption(
          option,
          clientExemptionData,
          dispositionOptionsToDisable,
          false
        );
      }),
    [hasStateTax, dispositionOptionsToDisable, clientExemptionData]
  );

  const transferTaxKindDisplay = useMemo(() => {
    if (!selectedTransferTaxKind) return undefined;
    return transferTaxKindOptions.find(
      (o) => o.value === selectedTransferTaxKind
    )?.display;
  }, [selectedTransferTaxKind, transferTaxKindOptions]);

  const distributionValue = useMemo(() => {
    if (!selectedDistributionKind) return '';
    return getDistributionSummary({
      dispositionKind_ButtonGroup: selectedDistributionKind,
      dispositionKind_Select: selectedDistributionKindSelect ?? '',
      dispositionPercentage: dispositionPercentage ?? null,
      dispositionAmount: dispositionAmount ?? null,
    } as DispositiveProvisionsFormRecipient);
  }, [
    selectedDistributionKind,
    selectedDistributionKindSelect,
    dispositionPercentage,
    dispositionAmount,
  ]);

  const forceExpansion = useMemo(() => {
    if (defaultExpandAll || forceExpandIds.includes(recipientId)) {
      return true;
    }
    // return undefined so the accordion uses its own default handling
    return undefined;
  }, [defaultExpandAll, forceExpandIds, recipientId]);

  return (
    <Box
      component="li"
      ref={setNodeRef}
      style={style}
      data-rbd-draggable-id={recipientId}
      data-testid={`BeneficiaryDesignationRecipient-${tier}-${index}`}
      sx={{
        listStyleType: 'none',
        p: 0,
        pt: index === 0 ? 0 : 1,
        m: 0,
      }}
    >
      {/* Rendered unconditionally (matching the DP recipient card): the modal
          owns the "create new" sub-modal state, and its handleAdd closes this
          dialog before opening a sub-modal. Conditionally mounting it would
          unmount the sub-modals before they can appear. */}
      <RecipientSelectorModal
        isOpen={isRecipientSelectorModalOpen}
        handleClose={() => setIsRecipientSelectorModalOpen(false)}
        householdId={householdId ?? ''}
        deadPrimaryClientId={null}
        deadPrimaryClientIds={primaryClients?.map((c) => c.id) ?? []}
        isTwoClientHousehold={isTwoClientHousehold}
        recipientConfiguration={recipientConfiguration}
        onRecipientCreated={(newRecipientId) =>
          setValue(
            `${basePath}.${index}.recipientId` as PathsOf<BeneficiaryDesignationsFormShape>,
            newRecipientId as never,
            { shouldDirty: true, shouldValidate: true }
          )
        }
      />
      <Accordion
        variant="filled"
        isDefaultExpanded={forceExpansion}
        id={recipientId}
        // Per design spec, BDG recipient cards use NAVY[50] (#F6F9FB)
        // background rather than the filled variant's GRAY[50].
        sx={{ '&.MuiAccordion-root': { backgroundColor: COLORS.NAVY[50] } }}
        labelContainerProps={{
          spacing: 0,
          justifyContent: 'center',
        }}
        leftAdornment={
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            paddingRight={1}
          >
            {canEditHouseholdData ? (
              <Draghandle {...listeners} {...attributes} />
            ) : null}
            <Stack
              padding="10px"
              bgcolor={tileBg}
              borderRadius="8px"
              justifyContent="center"
              alignItems="center"
            >
              {tileIconElement}
            </Stack>
          </Stack>
        }
        title={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="title-xs">{recipientDisplay}</Typography>
          </Stack>
        }
        subtitle={transferTaxKindDisplay}
        rightAdornment={
          distributionValue ? (
            <Stack spacing={0.5} alignItems="flex-end" minWidth={0}>
              <Typography
                variant="body-xs"
                noWrap
                title={distributionValue}
                sx={{ maxWidth: '100%' }}
              >
                {distributionValue}
              </Typography>
            </Stack>
          ) : undefined
        }
      >
        {/* key by index so the body's controlled inputs remount on reorder
            (rows persist by _id for drag) — matches DispositiveProvisionsRecipient. */}
        <Box key={index}>
          <FormLayoutRow>
            <FormLayoutItem width={7}>
              <Stack spacing={1}>
                <FormAwareTypeaheadSelectInput<BeneficiaryDesignationsFormShape>
                  control={control}
                  label="Recipient"
                  fieldName={
                    `${basePath}.${index}.recipientId` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                  }
                  options={typeaheadOptions}
                  groupBy={(option) => option.groupName ?? ''}
                  placeholder="Select recipient"
                  required
                  hideEmptyOption
                  // Recipient cards are draggable list items (position: relative;
                  // z-index), so each is its own stacking context. Portal the
                  // popper so it isn't painted under sibling recipient cards.
                  disablePortal={false}
                  // Adding a recipient auto-focuses this input; keep the menu
                  // closed on that focus so the row expands without the dropdown
                  // popping open (the user clicks or types to open it).
                  openOnFocus={false}
                  testId={`select-${basePath}.${index}.recipientId`}
                  addNewOption={
                    recipientConfiguration.createRecipientTypes.length
                      ? {
                          onClick: () => setIsRecipientSelectorModalOpen(true),
                        }
                      : undefined
                  }
                  startAdornment={
                    <InputAdornment position="start">
                      <User01Icon size={16} />
                    </InputAdornment>
                  }
                />
                {showOutrightDistributionCallout && (
                  <Callout severity="warning">
                    {isSurvivingSpouseRecipient
                      ? `Entity will distribute to ${calloutRecipientName ?? 'the surviving spouse'} outright. Consider distributing to ${calloutRecipientName ? `${calloutRecipientName}'s` : "the surviving spouse's"} retirement accounts or to an inherited retirement account.`
                      : `Entity will distribute to ${calloutRecipientName ?? 'the selected beneficiary'} outright. Consider distributing to an inherited retirement account.`}
                  </Callout>
                )}
              </Stack>
            </FormLayoutItem>
            <FormLayoutItem width={5}>
              <Stack spacing={1}>
                <FormAwareSelectInput<BeneficiaryDesignationsFormShape>
                  control={control}
                  label="Recipient type"
                  fieldName={
                    `${basePath}.${index}.transferTaxKind` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                  }
                  options={transferTaxKindOptions}
                  required
                />
                <ContextualHelpTrigger
                  display="Which type should I pick?"
                  contextualHelpId="dispositiveProvisionsRecipientType"
                  variant="helpText"
                  {...(entityId && { contextData: { entityId } })}
                />
              </Stack>
            </FormLayoutItem>
          </FormLayoutRow>

          <FormLayoutRow
            sx={{
              alignItems: 'end',
              marginBottom: 1,
            }}
          >
            <FormLayoutItem width={4}>
              <FormAwareButtonGroup<BeneficiaryDesignationsFormShape>
                label="Distribution type"
                fieldName={
                  `${basePath}.${index}.dispositionKind_ButtonGroup` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                }
                control={control}
                options={dispositionKindOptions}
                variant="light"
              />
            </FormLayoutItem>
            <FormLayoutItem width={8}>
              {(isPercentDistributionKind || isAmountDistributionKind) && (
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  <Box sx={{ flex: 1 }}>
                    {isPercentDistributionKind && (
                      <FormAwarePercentInput<BeneficiaryDesignationsFormShape>
                        label="Distribution percentage"
                        fieldName={
                          `${basePath}.${index}.dispositionPercentage` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                        }
                        control={control}
                        isDecimalJSInput
                        decimalScale={5}
                        fixedDecimalScale={false}
                      />
                    )}
                    {isAmountDistributionKind && (
                      <FormAwareCurrencyInput<BeneficiaryDesignationsFormShape>
                        label="Distribution amount"
                        fieldName={
                          `${basePath}.${index}.dispositionAmount` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                        }
                        control={control}
                        isDecimalJSInput
                      />
                    )}
                  </Box>
                  {advancedSettingsButton}
                </Stack>
              )}
              {isFormulaDistributionKind && (
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormAwareSelectInput<BeneficiaryDesignationsFormShape>
                      label="Distribution formula"
                      fieldName={
                        `${basePath}.${index}.dispositionKind_Select` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                      }
                      control={control}
                      options={dispositionKindSelectOptions}
                      required
                    />
                  </Box>
                  {advancedSettingsButton}
                </Stack>
              )}
              {isAssetDistributionKind && (
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  <Box sx={{ flex: 1 }}>
                    <AssetSelectorDropdown
                      recipientsBasePath={recipientsBasePath}
                      idx={index}
                    />
                  </Box>
                  {advancedSettingsButton}
                </Stack>
              )}
            </FormLayoutItem>
          </FormLayoutRow>

          {showAdvancedSettingsCard && (
            <AdvancedSettingsCard
              recipientsBasePath={recipientsBasePath}
              idx={index}
              isExpanded={advancedSettingsIsOpen}
              dispositionKind={selectedDistributionKind}
              showFormulaPercentage={isFormulaDistributionKind}
              selectedFormulaKind={selectedDistributionKindSelect}
              hideTransferToggle={isInsuranceOnlySelection}
            >
              {!residuaryPaysAllTaxes && bearsTaxCheckbox}
            </AdvancedSettingsCard>
          )}
          {showAdvancedSettingsCard && !isAssetDistributionKind && (
            <DistributionSummaryCallout
              distributionKind={getDistributionKindLabel({
                isAmountDistributionKind,
                isPercentDistributionKind,
              })}
              dispositionAmount={thisRecipient?.dispositionAmount}
              dispositionPercentage={thisRecipient?.dispositionPercentage}
              dispositionFormulaicPercentage={
                thisRecipient?.dispositionFormulaicPercentage
              }
              dispositionLimit={thisRecipient?.dispositionLimit}
              formulaKind={selectedDistributionKindSelect}
              bearsTax={thisRecipient?.bearsTax}
              growByInflation={thisRecipient?.growByInflation}
            />
          )}
          {isAssetDistributionKind && (
            <AssetDistributionCallout
              recipientsBasePath={recipientsBasePath}
              idx={index}
            />
          )}
          {/* When the advanced settings card is shown, the bearsTax checkbox
              renders inside it. */}
          {!showAdvancedSettingsCard && !residuaryPaysAllTaxes && (
            <FormLayoutRow>
              <FormLayoutItem width={12}>{bearsTaxCheckbox}</FormLayoutItem>
            </FormLayoutRow>
          )}

          <FormLayoutRow
            sx={{ display: notesAreExpanded ? 'initial' : 'none' }}
          >
            <FormLayoutItem width={12}>
              <FormAwareTextInput<BeneficiaryDesignationsFormShape>
                label="Notes"
                multiline
                fieldName={
                  `${basePath}.${index}.notes` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>
                }
                control={control}
                rows={3}
                resizable
              />
            </FormLayoutItem>
          </FormLayoutRow>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            mt={2}
          >
            <Box>
              {!notesAreExpanded && (
                <Button
                  variant="transparent"
                  size="sm"
                  startIcon={PlusIcon}
                  disabled={disabled || !canEditHouseholdData}
                  onClick={() => setNotesAreExpanded(true)}
                >
                  Add notes
                </Button>
              )}
            </Box>
            {canEditHouseholdData && (
              <Box>
                <Button
                  variant="destructive-transparent"
                  size="sm"
                  startIcon={Trash01Icon}
                  onClick={onRemove}
                >
                  Remove recipient
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      </Accordion>
    </Box>
  );
}

function getDistributionKindLabel({
  isAmountDistributionKind,
  isPercentDistributionKind,
}: {
  isAmountDistributionKind: boolean;
  isPercentDistributionKind: boolean;
}): 'amount' | 'percentage' | 'formula' {
  if (isAmountDistributionKind) return 'amount';
  if (isPercentDistributionKind) return 'percentage';
  return 'formula';
}
